/**
 * petCareGate — single source of truth for "which pets are unlocked
 * at each plan tier" for both the Pet Care Schedule (vet/flea/bath/
 * litter) AND the daily walk log/planner.
 *
 * Tier cap (applies to dog & cat walks + care):
 *   · Free  → 1 dog or cat  (the oldest added)
 *   · Plus  → 2 dogs or cats (oldest two)
 *   · Grand → 3 dogs or cats (oldest three)
 *   · Extended → every dog & cat (uncapped)
 *
 * Ranking: sort_order asc, then created_at asc, then id asc — mirrors
 * `_care_allowed_pet_ids` on the backend exactly so UI and server
 * never disagree on which pets are unlocked.
 *
 * Feeds and water changes stay free for every pet on every tier.
 */

const CARE_SPECIES = ["dog", "cat"];

const TIER_CAPS = {
  free: 1,
  plus: 2,
  grand: 3,
  extended: Infinity,
};

function careCap(tier) {
  return TIER_CAPS[tier] ?? TIER_CAPS.free;
}

function rankedCarePets(members) {
  return (members || [])
    .filter((m) => m && m.role === "pet" && CARE_SPECIES.includes(m.pet_species))
    .slice()
    .sort((a, b) => {
      const s = (a.sort_order ?? 0) - (b.sort_order ?? 0);
      if (s) return s;
      const c = (a.created_at || "").localeCompare(b.created_at || "");
      if (c) return c;
      return (a.id || "").localeCompare(b.id || "");
    });
}

/**
 * Back-compat helper — returns the id of the first eligible pet (rank 0).
 * Kept because other code still imports it.
 */
export function primaryCarePetId(members) {
  return rankedCarePets(members)[0]?.id || null;
}

/**
 * Set of pet ids that currently have the rich care log unlocked at
 * `tier`. Pets beyond the cap are locked.
 */
export function allowedCarePetIds(members, tier) {
  const cap = careCap(tier || "free");
  const ranked = rankedCarePets(members);
  const allowed = new Set();
  for (let i = 0; i < ranked.length && i < cap; i++) {
    allowed.add(ranked[i].id);
  }
  return allowed;
}

export function isPetCareLockedForPet(pet, members, tier) {
  if (!pet || pet.role !== "pet") return false;
  if (!CARE_SPECIES.includes(pet.pet_species)) return false;
  const cap = careCap(tier || "free");
  if (cap === Infinity) return false;
  return !allowedCarePetIds(members, tier).has(pet.id);
}

/**
 * Walks (logging + planning) share the same tier cap as the Pet Care
 * Schedule — the oldest dog/cat is always covered, additional dogs/
 * cats are locked behind a plan upgrade. Non-dog/cat species are
 * never gated for walks.
 */
export function isPetWalkLockedForPet(pet, members, tier) {
  return isPetCareLockedForPet(pet, members, tier);
}

/**
 * Short human label like "1 dog or cat" / "2 dogs or cats" / "every
 * dog & cat" — used in pricing copy and upgrade-nudge tooltips.
 */
export function careCapLabel(tier) {
  const cap = careCap(tier || "free");
  if (cap === Infinity) return "every dog & cat";
  return cap === 1 ? "1 dog or cat" : `${cap} dogs or cats`;
}

/**
 * The minimum tier needed to unlock an Nth-in-rank pet. Used to tailor
 * upgrade-nudge copy ("Upgrade to Plus / Grand to track care for …").
 */
export function nextTierForRank(rank) {
  if (rank < TIER_CAPS.free) return null;        // already covered
  if (rank < TIER_CAPS.plus) return "plus";      // rank 1 (second pet)
  if (rank < TIER_CAPS.grand) return "grand";    // rank 2 (third pet)
  return "extended";                             // rank 3+ (fourth pet onwards)
}
